import React, { useCallback, useEffect, useState } from 'react';
import { api, getMe } from '../lib/api';

/* Admins only. Every pair as the matcher sees it — score, what blocked it, the
   judge's verdict — with good/bad buttons. Those labels are the test set every
   matcher change is measured against. */

const MODE_TEXT = {
  rules: 'Rules only — the scorers decide.',
  shadow: 'Shadow — the scorers decide; the AI judge runs alongside so you can compare.',
  judge: 'Judge — the AI judge decides; the rules take over if it is down.',
};

function pct(v) {
  return v === null || v === undefined ? '—' : `${Math.round(v * 100)}%`;
}

function Worth({ v, aId, side }) {
  if (!v) return null;
  const mine = v.member_a_id === aId ? (side === 'a' ? v.worth_for_a : v.worth_for_b)
    : (side === 'a' ? v.worth_for_b : v.worth_for_a);
  return <b className={`mr-worth w${mine}`}>{mine}/5</b>;
}

export default function MatchingReview({ notify, go }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [filter, setFilter] = useState('all');

  const load = useCallback(() => {
    api.matchingOverview().then(setData).catch((e) => notify?.(e.message));
  }, [notify]);

  useEffect(load, [load]);

  if (!data) return <div className="page"><p className="muted-sm">Loading matching…</p></div>;

  async function act(key, fn, ok) {
    setBusy(key);
    try {
      await fn();
      if (ok) notify?.(ok);
      load();
    } catch (e) {
      notify?.(e.message);
    } finally {
      setBusy('');
    }
  }

  const ev = data.evaluation;
  const pairs = data.pairs.filter((p) => (filter === 'all' ? true
    : filter === 'open' ? !p.blocked_by : filter === 'blocked' ? Boolean(p.blocked_by) : p.labels.length));

  return (
    <div className="page matching-review">
      <div className="admin-head">
        <h1 className="sec-title">Admin</h1>
        <div className="admin-tabs">
          <button className="feed-view" onClick={() => go?.('admin')}>Dashboard</button>
          <button className="feed-view on" aria-current="page">Matching review</button>
        </div>
      </div>

      <section className="card pad mr-top">
        <div className="mr-mode">
          <b>Mode</b>
          {data.modes.map((m) => (
            <button key={m} className={`btn sm ${data.mode === m ? 'primary' : 'ghost'}`} disabled={busy === 'mode'}
              onClick={() => act('mode', () => api.setMatchingMode(m), `Mode: ${m}`)}>{m}</button>
          ))}
        </div>
        <p className="muted-sm">{MODE_TEXT[data.mode]}</p>

        <div className="mr-stats">
          <div><b>{ev.labelled_pairs}</b><span>pairs labelled</span></div>
          <div><b>{pct(ev.rules.agreement)}</b><span>rules agree with you</span></div>
          <div><b>{pct(ev.judge.agreement)}</b><span>judge agrees with you</span></div>
          <div><b>{data.weights_version}</b><span>scorer weights</span></div>
        </div>
        <p className="muted-sm">
          Label 20–30 pairs good or bad. Switch to <b>judge</b> once it agrees with you more often than the rules.
          {data.learning?.reason ? ` Learning: ${data.learning.reason}.` : ''}
        </p>
        <div className="mr-mode">
          <button className="btn ghost sm" disabled={busy === 'run'}
            onClick={() => act('run', () => api.runMatching(), 'Matching run finished')}>Run matching now</button>
          <button className="btn ghost sm" disabled={busy === 'learn'}
            onClick={() => act('learn', () => api.learnMatching(), 'Learning run finished')}>Re-learn weights</button>
        </div>
      </section>

      <div className="mr-filter">
        {[['all', 'All'], ['open', 'Could be introduced'], ['blocked', 'Blocked'], ['labelled', 'Labelled']].map(([k, t]) => (
          <button key={k} className={`btn sm ${filter === k ? 'primary' : 'quiet'}`} onClick={() => setFilter(k)}>{t}</button>
        ))}
      </div>

      {pairs.map((p) => {
        const myLabel = p.labels.find((l) => String(l.by) === String(getMe()?.id))?.verdict;
        return (
          <article className="card pad mr-pair" key={p.pair_key}>
            <div className="mr-people">
              {[['a', p.a], ['b', p.b]].map(([side, m]) => (
                <div key={m.id} className="mr-person">
                  <b>{m.name}</b> {m.protected && <span className="mr-tag">protected</span>}
                  <Worth v={p.verdict} aId={p.a.id} side={side} />
                  <span className="muted-sm">{m.headline}</span>
                  <span className="muted-sm">needs: {m.needs.join(', ') || '—'} · gives: {m.gives.join(', ') || '—'}</span>
                  <button className="linklike" disabled={busy === `p-${m.id}`}
                    onClick={() => act(`p-${m.id}`, () => api.protectMember(m.id, !m.protected),
                      m.protected ? 'Unprotected' : 'Protected')}>
                    {m.protected ? 'unprotect' : 'protect'}
                  </button>
                </div>
              ))}
            </div>

            <div className="mr-line">
              <span className="mr-score">score {p.score}</span>
              {p.blocked_by ? <span className="mr-blocked">blocked: {p.blocked_by}</span>
                : <span className={p.rules_would_introduce ? 'mr-yes' : 'mr-no'}>
                  rules: {p.rules_would_introduce ? 'introduce' : 'no'}</span>}
              {p.judge_would_introduce !== null && (
                <span className={p.judge_would_introduce ? 'mr-yes' : 'mr-no'}>
                  judge: {p.judge_would_introduce ? 'introduce' : 'no'}</span>
              )}
              {p.proposal && <span className="muted-sm">intro: {p.proposal.state} ({p.proposal.engine})</span>}
            </div>
            {p.why.length > 0 && <p className="muted-sm">{p.why.join(' · ')}</p>}
            {p.verdict && (
              <div className="mr-verdict">
                <p><b>To {p.a.name.split(' ')[0]}:</b> {p.verdict.member_a_id === p.a.id ? p.verdict.reason_for_a : p.verdict.reason_for_b}</p>
                <p><b>To {p.b.name.split(' ')[0]}:</b> {p.verdict.member_a_id === p.a.id ? p.verdict.reason_for_b : p.verdict.reason_for_a}</p>
                {p.verdict.opener && <p className="muted-sm">Opener: {p.verdict.opener}</p>}
              </div>
            )}

            <div className="mr-actions">
              <button className={`btn sm ${myLabel === 'good' ? 'primary' : 'ghost'}`} disabled={busy === p.pair_key}
                onClick={() => act(p.pair_key, () => api.labelPair(p.a.id, p.b.id, 'good'))}>Good intro</button>
              <button className={`btn sm ${myLabel === 'bad' ? 'primary' : 'ghost'}`} disabled={busy === p.pair_key}
                onClick={() => act(p.pair_key, () => api.labelPair(p.a.id, p.b.id, 'bad'))}>Bad intro</button>
              {!p.verdict && (
                <button className="btn quiet sm" disabled={busy === `j-${p.pair_key}`}
                  onClick={() => act(`j-${p.pair_key}`, () => api.judgePair(p.a.id, p.b.id), 'Judged')}>
                  {busy === `j-${p.pair_key}` ? 'Judging…' : 'Ask the judge'}
                </button>
              )}
            </div>
          </article>
        );
      })}
      {pairs.length === 0 && <p className="muted-sm">Nothing here.</p>}
    </div>
  );
}
